import React, { useState } from 'react';
import {
  Box, Typography, Stack, Paper, Table, TableHead, TableRow, TableCell, Chip, IconButton, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Select, FormControl,
  InputLabel, CircularProgress, Alert, Snackbar, Tooltip, Switch,
  FormControlLabel,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useQuery, useMutation } from '@apollo/client';
import {
  ALL_NOTIFICATION_TRIGGERS_QUERY,
  CREATE_NOTIFICATION_TRIGGER_MUTATION,
  UPDATE_NOTIFICATION_TRIGGER_MUTATION,
  DELETE_NOTIFICATION_TRIGGER_MUTATION,
  ALL_EMAIL_TEMPLATES_QUERY,
} from '../graphql/queries';

const EVENT_TYPE_SUGGESTIONS = [
  'user_registered', 'interest_sent', 'interest_accepted', 'interest_rejected',
  'message_received', 'meeting_scheduled', 'meeting_cancelled',
  'contract_created', 'contract_signed', 'contract_updated',
  'wishlist_submitted', 'wishlist_approved', 'wishlist_rejected',
  'payment_received', 'payment_overdue',
  'profile_viewed', 'profile_shortlisted',
  'ceremony_reminder', 'task_due', 'task_overdue',
];

const RECIPIENT_TYPES = ['user', 'admin', 'both', 'contractor', 'all_users'];

const EMPTY_FORM = {
  name: '',
  eventType: '',
  templateId: '',
  description: '',
  isActive: true,
  sendEmail: true,
  sendInApp: true,
  recipientType: 'user',
};

export default function AdminNotificationTriggersPage() {
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const [delConfirm, setDelConfirm] = useState(null);

  const { data, loading, error, refetch } = useQuery(ALL_NOTIFICATION_TRIGGERS_QUERY, {
    fetchPolicy: 'network-only',
  });
  const { data: tmplData } = useQuery(ALL_EMAIL_TEMPLATES_QUERY, { fetchPolicy: 'cache-and-network' });

  const [createTrigger, { loading: creating }] = useMutation(CREATE_NOTIFICATION_TRIGGER_MUTATION, {
    onCompleted: () => { setDlgOpen(false); refetch(); toast('Trigger created'); },
    onError: (e) => toast(e.message, 'error'),
  });
  const [updateTrigger, { loading: updating }] = useMutation(UPDATE_NOTIFICATION_TRIGGER_MUTATION, {
    onCompleted: () => { setDlgOpen(false); refetch(); toast('Trigger updated'); },
    onError: (e) => toast(e.message, 'error'),
  });
  const [deleteTrigger] = useMutation(DELETE_NOTIFICATION_TRIGGER_MUTATION, {
    onCompleted: () => { setDelConfirm(null); refetch(); toast('Trigger deleted'); },
    onError: (e) => toast(e.message, 'error'),
  });

  function toast(msg, severity = 'success') {
    setSnack({ open: true, msg, severity });
  }

  function openNew() {
    setEditItem(null);
    setForm(EMPTY_FORM);
    setDlgOpen(true);
  }

  function openEdit(trigger) {
    setEditItem(trigger);
    setForm({
      name: trigger.name,
      eventType: trigger.eventType,
      templateId: trigger.templateId || '',
      description: trigger.description || '',
      isActive: trigger.isActive,
      sendEmail: trigger.sendEmail,
      sendInApp: trigger.sendInApp,
      recipientType: trigger.recipientType || 'user',
    });
    setDlgOpen(true);
  }

  function handleSave() {
    const variables = {
      name: form.name.trim(),
      eventType: form.eventType.trim(),
      templateId: form.templateId || null,
      description: form.description.trim() || null,
      isActive: form.isActive,
      sendEmail: form.sendEmail,
      sendInApp: form.sendInApp,
      recipientType: form.recipientType,
    };
    if (editItem) {
      updateTrigger({ variables: { id: editItem.id, ...variables } });
    } else {
      createTrigger({ variables });
    }
  }

  const triggers = data?.allNotificationTriggers || [];
  const templates = tmplData?.allEmailTemplates || [];

  // Derive existing event types for autocomplete
  const existingEventTypes = [...new Set(triggers.map(t => t.eventType))];
  const allEventTypes = [...new Set([...EVENT_TYPE_SUGGESTIONS, ...existingEventTypes])].sort();

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <NotificationsActiveIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Notification Triggers</Typography>
            <Typography variant="body2" color="text.secondary">
              Define event types that fire notifications and emails automatically
            </Typography>
          </Box>
        </Stack>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
          Add Trigger
        </Button>
      </Stack>

      {/* Summary */}
      <Stack direction="row" spacing={2} mb={3}>
        <Chip label={`${triggers.length} Total`} color="default" />
        <Chip label={`${triggers.filter(t => t.isActive).length} Active`} color="success" />
        <Chip label={`${triggers.filter(t => t.sendEmail).length} Email`} color="info" />
        <Chip label={`${triggers.filter(t => t.sendInApp).length} In-App`} color="primary" />
      </Stack>

      {/* Table */}
      {loading && <CircularProgress sx={{ display: 'block', mx: 'auto', mt: 4 }} />}
      {error && <Alert severity="error">{error.message}</Alert>}
      {!loading && !error && (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Event Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Email Template</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Recipient</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Email</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">In-App</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {triggers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No triggers found. Click "Add Trigger" to create one.
                  </TableCell>
                </TableRow>
              )}
              {triggers.map((trigger) => (
                <TableRow key={trigger.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{trigger.name}</Typography>
                    {trigger.description && (
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                        {trigger.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={trigger.eventType} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />
                  </TableCell>
                  <TableCell>
                    {trigger.template ? (
                      <Tooltip title={trigger.template.subject}>
                        <Chip label={trigger.template.name} size="small" color="info" variant="outlined" />
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.disabled">None</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={trigger.recipientType} size="small" sx={{ textTransform: 'capitalize' }} />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={trigger.sendEmail ? 'Yes' : 'No'}
                      size="small"
                      color={trigger.sendEmail ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={trigger.sendInApp ? 'Yes' : 'No'}
                      size="small"
                      color={trigger.sendInApp ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={trigger.isActive ? 'Active' : 'Inactive'}
                      color={trigger.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(trigger)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setDelConfirm(trigger)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editItem ? 'Edit Trigger' : 'New Notification Trigger'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} mt={1}>
            <TextField
              label="Trigger Name *"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
              helperText="Human-readable name, e.g. 'User Registration Notification'"
            />
            <FormControl fullWidth>
              <InputLabel>Event Type *</InputLabel>
              <Select
                value={form.eventType}
                label="Event Type *"
                onChange={(e) => setForm({ ...form, eventType: e.target.value })}
              >
                {allEventTypes.map((et) => (
                  <MenuItem key={et} value={et} sx={{ fontFamily: 'monospace' }}>{et}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Or enter custom event type"
              value={form.eventType}
              onChange={(e) => setForm({ ...form, eventType: e.target.value })}
              fullWidth
              placeholder="e.g. custom_event_name"
              helperText="Use snake_case for event types"
              size="small"
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              multiline rows={2}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Email Template</InputLabel>
              <Select
                value={form.templateId}
                label="Email Template"
                onChange={(e) => setForm({ ...form, templateId: e.target.value })}
              >
                <MenuItem value="">None</MenuItem>
                {templates.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.name} — {t.subject}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Recipient Type</InputLabel>
              <Select
                value={form.recipientType}
                label="Recipient Type"
                onChange={(e) => setForm({ ...form, recipientType: e.target.value })}
              >
                {RECIPIENT_TYPES.map((rt) => (
                  <MenuItem key={rt} value={rt} sx={{ textTransform: 'capitalize' }}>{rt}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.sendEmail}
                    onChange={(e) => setForm({ ...form, sendEmail: e.target.checked })}
                  />
                }
                label="Send Email"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.sendInApp}
                    onChange={(e) => setForm({ ...form, sendInApp: e.target.checked })}
                  />
                }
                label="Send In-App"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!form.name || !form.eventType || creating || updating}
          >
            {creating || updating ? <CircularProgress size={18} /> : (editItem ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!delConfirm} onClose={() => setDelConfirm(null)} maxWidth="xs">
        <DialogTitle>Delete Trigger?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <b>{delConfirm?.name}</b> ({delConfirm?.eventType})? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDelConfirm(null)}>Cancel</Button>
          <Button
            color="error" variant="contained"
            onClick={() => deleteTrigger({ variables: { id: delConfirm.id } })}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
